import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeRoomCode } from "../convex/lib/game";
import {
  useAssignment,
  useAssignmentKey,
  useDubs,
  useHeartbeat,
  usePhaseActions,
  useRoomActions,
  useRoomState,
  useRoundScores,
  useSignedUrls,
  useSkipTally,
  useStandings,
} from "./components/backend";
import type {
  Auth,
  Clip,
  PlaybackEntry,
  RoomState,
  Session,
  ToastTone,
  VoteCandidate,
} from "./components/contracts";
import { Footer } from "./components/Footer";
import { NavBar } from "./components/NavBar";
import { Panel } from "./components/Panel";
import { ToastStack, useToasts } from "./components/Toast";
import { useClerk, useUser } from "@clerk/clerk-react";
import { Home } from "./screens/Home";
import { SignIn } from "./screens/SignIn";
import { Lobby } from "./screens/Lobby";
import { Playback } from "./screens/Playback";
import { Podium } from "./screens/Podium";
import { Record } from "./screens/Record";
import { Scoreboard } from "./screens/Scoreboard";
import { SegmentPicker } from "./screens/SegmentPicker";
import { Vote } from "./screens/Vote";
import { errorText } from "./components/util";

const SESSION_KEY = "badly-dubbed.session";
const HEARTBEAT_MS = 8_000;

function loadSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (parsed.playerId && parsed.token && parsed.roomId) return parsed as Session;
  } catch {
    /* Corrupt or blocked storage — fall through to a clean slate. */
  }
  return null;
}

/**
 * Routing is the room's `phase` plus one piece of local state (the segment
 * picker, which is a lobby detour, not a phase). No router library: there are
 * no URLs to share beyond the invite link, and history navigation would let a
 * player "back" out of a live recording.
 */
export default function App() {
  const [session, setSession] = useState<Session | null>(loadSession);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const { toasts, push, dismiss } = useToasts();

  /**
   * Accounts are strictly additive. `useUser` reports null when Clerk is not
   * configured or has not loaded, and every use below tolerates that — the
   * game must stay fully playable with the auth provider absent.
   */
  const clerkEnabled = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
  const { user } = useUser();
  const clerk = useClerk();
  const accountLabel =
    user?.firstName ?? user?.primaryEmailAddress?.emailAddress?.split("@")[0] ?? null;

  const auth: Auth | null = useMemo(
    () => (session ? { playerId: session.playerId, token: session.token } : null),
    [session],
  );

  const room = useRoomState(session?.roomId ?? null);
  const beat = useHeartbeat(auth);
  const { create, join } = useRoomActions();

  const inviteCode = useMemo(
    () => normalizeRoomCode(new URLSearchParams(window.location.search).get("room") ?? ""),
    [],
  );

  const persist = useCallback((next: Session | null) => {
    setSession(next);
    try {
      if (next) window.localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      else window.localStorage.removeItem(SESSION_KEY);
    } catch {
      /* Private mode. The seat still works for this tab. */
    }
  }, []);

  const toast = useCallback(
    (text: string, tone: ToastTone = "info") => void push(text, tone),
    [push],
  );

  /** Presence. A player who stops beating is skipped, not blocked on. */
  useEffect(() => {
    if (!auth) return;
    beat();
    const id = window.setInterval(beat, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [auth, beat]);

  /** A room that vanished server-side should not strand the client. */
  useEffect(() => {
    if (session && room === null) {
      toast("That room is gone.", "error");
      persist(null);
    }
  }, [room, session, persist, toast]);

  async function handleCreate(name: string, soloTest = false) {
    setJoining(true);
    setJoinError(null);
    try {
      const r = await create(name, soloTest);
      persist({ playerId: r.playerId, token: r.token, roomId: r.roomId, code: r.code, name });
    } catch (err) {
      setJoinError(errorText(err, "Couldn't create a room."));
    } finally {
      setJoining(false);
    }
  }

  async function handleJoin(code: string, name: string) {
    setJoining(true);
    setJoinError(null);
    try {
      const r = await join(code, name);
      persist({ playerId: r.playerId, token: r.token, roomId: r.roomId, code: r.code, name });
    } catch (err) {
      setJoinError(errorText(err, "No room with that code."));
    } finally {
      setJoining(false);
    }
  }

  const body = (() => {
    if (showSignIn) {
      return (
        <SignIn
          onDone={() => setShowSignIn(false)}
          onCancel={() => setShowSignIn(false)}
          onToast={toast}
        />
      );
    }
    if (!session || !auth) {
      return (
        <Home
          initialCode={inviteCode}
          busy={joining}
          error={joinError}
          onCreate={(name, soloTest) => void handleCreate(name, soloTest)}
          onJoin={(code, name) => void handleJoin(code, name)}
          onSignIn={clerkEnabled ? () => setShowSignIn(true) : undefined}
          account={accountLabel}
          onSignOut={() => void clerk.signOut()}
        />
      );
    }
    if (room === undefined) return <Interstitial title="Connecting" line="Finding your room…" />;
    if (room === null) return <Interstitial title="Gone" line="That room no longer exists." />;

    return (
      <RoomSession
        room={room}
        auth={auth}
        onLeave={() => persist(null)}
        onToast={toast}
      />
    );
  })();

  return (
    <>
      {body}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </>
  );
}

/**
 * Everything that needs a live room. Split out so the hooks below only ever
 * run with a real `roomId` — Convex `"skip"` handles the rest.
 */
function RoomSession({
  room,
  auth,
  onLeave,
  onToast,
}: {
  room: RoomState;
  auth: Auth;
  onLeave: () => void;
  onToast: (text: string, tone?: ToastTone) => void;
}) {
  const [pickingClipId, setPickingClipId] = useState<string | null>(null);
  const phases = usePhaseActions();

  const me = room.players.find((p) => p.playerId === auth.playerId);
  const isHost = me?.isHost ?? false;

  /**
   * Who may drive the room forward. Normally the host — but once the host has
   * gone quiet past the heartbeat timeout the server widens this to everyone,
   * so a sleeping phone can't freeze the game. This mirrors `requireRoomControl`
   * on the server; the server is still the one enforcing it.
   */
  const canControl = isHost || room.controlOpen;
  /** True only in the widened case, so screens can explain the extra button. */
  const hostAbsent = !isHost && room.controlOpen;

  const assignment = useAssignment(auth);
  const assignmentKey = useAssignmentKey(auth);
  const dubs = useDubs(room.roomId, room.round);
  const skip = useSkipTally(auth, room.roomId, room.round);
  const roundScores = useRoundScores(room.roomId, room.round);
  const standings = useStandings(room.phase === "podium" ? room.roomId : null);

  const pickingClip = room.clips.find((c) => c.clipId === pickingClipId) ?? null;

  /**
   * Every R2 object this screen could need, signed in one round trip. Keys are
   * collected up front because `r2.getSignedUrls` is an action — it cannot be
   * subscribed to, and calling it per-element would fan out badly.
   */
  const keys = useMemo(() => {
    const out = new Set<string>();
    if (pickingClip) out.add(pickingClip.r2Key);
    if (assignmentKey) out.add(assignmentKey);
    if (room.phase === "playback") {
      const byId = new Map(room.clips.map((c) => [c.clipId, c]));
      for (const dub of dubs ?? []) {
        const clip = byId.get(dub.clipId);
        if (clip) out.add(clip.r2Key);
        for (const chunk of dub.chunks) out.add(chunk.r2Key);
      }
    }
    return [...out];
  }, [pickingClip, assignmentKey, room.phase, room.clips, dubs]);

  const urls = useSignedUrls(auth, keys);

  /**
   * The assignment with its signed clip URL folded in, memoised.
   *
   * This used to be built inline in the JSX, which handed the Record screen a
   * brand-new object — and therefore a brand-new `subChunks` array — on every
   * single render. Anything downstream keyed on those references restarted on
   * unrelated re-renders. Record no longer depends on the object identity, but
   * a stable reference is the actual fix and stops the same trap being reset
   * by the next person to add an effect there.
   */
  const liveAssignment = useMemo(
    () =>
      assignment
        ? { ...assignment, clipUrl: assignmentKey ? urls[assignmentKey] : undefined }
        : null,
    [assignment, assignmentKey, urls],
  );

  /**
   * Whoever holds control deals as soon as the room lands in `dealing`. If the
   * host dropped between the scoreboard and the deal, a remaining player picks
   * it up — otherwise the room sits on the shuffling screen forever. Concurrent
   * callers are harmless: the server's phase check rejects all but the first.
   */
  useEffect(() => {
    if (room.phase !== "dealing" || !canControl) return;
    void phases.deal(auth).catch(() => {
      /* Another player got there first, or the room already moved on. */
    });
  }, [room.phase, canControl, auth, phases]);

  const advance = useCallback(async () => {
    await phases.advance(auth, room.phase);
  }, [phases, auth, room.phase]);

  /** Host-only. Moves the room's shared playback cursor; followers just watch. */
  const setPlaybackIndex = useCallback(
    async (index: number) => {
      await phases.setPlaybackIndex(auth, index);
    },
    [phases, auth],
  );

  const candidates: VoteCandidate[] = room.players.map((p) => ({
    playerId: p.playerId,
    name: p.name,
    isSelf: p.playerId === auth.playerId,
  }));

  const entries: PlaybackEntry[] = useMemo(() => {
    if (!dubs || dubs.length === 0) return [];
    const byId = new Map(room.clips.map((c) => [c.clipId, c]));

    const dubEntries: PlaybackEntry[] = dubs.map((dub) => {
      const clip = byId.get(dub.clipId);
      return {
        playerId: dub.playerId,
        performerName: dub.name,
        clipUrl: clip ? urls[clip.r2Key] : undefined,
        segmentStart: clip?.segmentStart ?? 0,
        audioUrls: [...dub.chunks]
          .sort((a, b) => a.subChunkIndex - b.subChunkIndex)
          .map((c) => urls[c.r2Key])
          .filter((u): u is string => Boolean(u)),
      };
    });

    // ASSUMPTION: "the original" is the clip behind the first dub in the
    // playback order. The backend tracks one `originalSkipped` flag per round,
    // not one per clip, so a single original is the only reading that fits.
    const first = byId.get(dubs[0].clipId);
    const original: PlaybackEntry = {
      playerId: null,
      performerName: "The original",
      clipUrl: first ? urls[first.r2Key] : undefined,
      segmentStart: first?.segmentStart ?? 0,
      audioUrls: [],
    };

    // The original is ALWAYS index 0, even once the room has voted to skip it.
    // Dropping it from the array when the skip passes would shorten the list
    // under the shared `playbackIndex`, silently shifting every device onto a
    // different entry and swallowing the first dub entirely. A passed skip ends
    // the original's playback instead (see Playback.tsx); the running order
    // itself has to stay a fixed shape for a shared cursor to mean anything.
    return [original, ...dubEntries];
  }, [dubs, room.clips, urls]);

  if (pickingClip) {
    return (
      <SegmentPicker
        auth={auth}
        clip={{ ...pickingClip, url: urls[pickingClip.r2Key] }}
        segmentLength={room.segmentLength}
        onDone={() => setPickingClipId(null)}
        onToast={onToast}
      />
    );
  }

  switch (room.phase) {
    case "lobby":
      return (
        <Lobby
          room={room}
          auth={auth}
          onPickSegment={(clip: Clip) => setPickingClipId(clip.clipId)}
          onLeave={onLeave}
          onToast={onToast}
        />
      );

    case "dealing":
      return (
        <Interstitial
          title="Shuffling the deck"
          line="Handing out clips. You will never get your own."
        />
      );

    case "recording":
      if (assignment === undefined) {
        return <Interstitial title="Dealing you in" line="Fetching your clip…" />;
      }
      if (assignment === null) {
        return (
          <Interstitial
            title="Sitting this one out"
            line="You have no clip this round. Hang tight."
          />
        );
      }
      return (
        <Record
          auth={auth}
          round={room.round}
          assignment={liveAssignment ?? assignment}
          canControl={canControl}
          hostAbsent={hostAbsent}
          onAdvance={advance}
          onToast={onToast}
        />
      );

    case "playback":
      return (
        <Playback
          auth={auth}
          round={room.round}
          canControl={canControl}
          hostAbsent={hostAbsent}
          segmentLength={room.segmentLength}
          entries={entries}
          playbackIndex={room.playbackIndex}
          skip={skip}
          onSetIndex={setPlaybackIndex}
          onAdvance={advance}
          onToast={onToast}
        />
      );

    case "voting":
      return (
        <Vote auth={auth} round={room.round} candidates={candidates} onToast={onToast} />
      );

    case "scoreboard":
      if (!roundScores) return <Interstitial title="Tallying" line="Counting the votes…" />;
      return (
        <Scoreboard
          playerId={auth.playerId}
          round={room.round}
          roundCount={room.roundCount}
          rows={roundScores}
          canControl={canControl}
          hostAbsent={hostAbsent}
          onAdvance={advance}
          onToast={onToast}
        />
      );

    case "podium":
      if (!standings) return <Interstitial title="Final tally" line="Building the podium…" />;
      return <Podium playerId={auth.playerId} rows={standings} onPlayAgain={onLeave} />;
  }
}

function Interstitial({ title, line }: { title: string; line: string }) {
  return (
    <div className="bd-screen">
      <NavBar />
      <main className="shell">
        <Panel title={title}>
          <p className="t-body center" role="status" aria-live="polite">
            {line}
          </p>
          <div className="bd-dots" aria-hidden="true">
            <span className="bd-dot is-active" />
            <span className="bd-dot" />
            <span className="bd-dot" />
          </div>
        </Panel>
      </main>
      <Footer />
    </div>
  );
}
